import { useEffect, useState } from "react";
import { Printer, Save, TestTube2, RefreshCw, CheckCircle2, AlertTriangle, Wifi, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const MODELS = [
  "QL-810W", "QL-820NWB", "QL-720NW", "QL-710W", "QL-700", "QL-1110NWB",
  "QL-1100", "QL-600", "QL-550", "QL-570", "QL-580N", "QL-650TD",
];
const LABEL_SIZES = [
  { value: "62", label: "62mm continuous" },
  { value: "62red", label: "62mm continuous (red/black)" },
  { value: "29x90", label: "29 x 90mm address" },
  { value: "38x90", label: "38 x 90mm" },
  { value: "12", label: "12mm continuous" },
  { value: "29", label: "29mm continuous" },
];

export default function PrinterSetupPanel() {
  const [status, setStatus] = useState(null);
  const [ip, setIp] = useState("");
  const [model, setModel] = useState("QL-810W");
  const [labelSize, setLabelSize] = useState("62");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  // Print-mode toggle: "airprint" (new default) prints from the iPad/browser
  // using iOS AirPrint. "cloud" tries to TCP from the backend (only works if
  // the printer is reachable from the cloud — usually it isn't).
  // Mode is persisted SERVER-SIDE (db.config.default.visitor_printer.print_mode)
  // so the backend knows whether to skip the cloud TCP attempt and avoid
  // flagging every visitor card with a misleading "Print failed" badge.
  const [mode, setMode] = useState("airprint");

  const load = async () => {
    try {
      const r = await api.get("/admin/visitors/printer-status");
      setStatus(r.data);
      setIp(r.data?.ip || "");
      setModel(r.data?.model || "QL-810W");
      setLabelSize(r.data?.label_size || "62");
      setMode(r.data?.print_mode || "airprint");
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/visitors/printer-config", {
        ip: ip.trim(), model, label_size: labelSize, print_mode: mode,
      });
      toast.success(ip.trim() ? "Printer saved." : "Printer cleared.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Switching modes persists immediately so the backend stops/starts the
  // cloud TCP attempt without needing the admin to click Save.
  const switchMode = async (next) => {
    if (next === mode) return;
    setMode(next);
    try {
      await api.put("/admin/visitors/printer-config", {
        ip: ip.trim(), model, label_size: labelSize, print_mode: next,
      });
      toast.success(next === "airprint" ? "Switched to AirPrint mode." : "Switched to Cloud IP mode.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch mode");
    }
  };

  // Cloud-IP test (legacy): asks backend to TCP-print directly. Won't work from cloud.
  const testPrintCloud = async () => {
    setTesting(true);
    try {
      await api.post("/admin/visitors/printer-test");
      toast.success("Test page sent! Check the printer.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  // AirPrint test: fetches the 62×100mm PDF (declared /MediaBox dimensions)
  // and opens it in a new Safari tab. iOS reads the PDF page size and
  // prints exactly one 62×100mm label — unlike PNG, where iOS ignores DPI
  // and splits into 2 pages.
  const testPrintAirPrint = async () => {
    try {
      const token = sessionStorage.getItem("rrdc_token") || "";
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/visitors/test-label-pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        toast.error("Pop-up blocked. Allow pop-ups for this site (Settings → Safari → Block Pop-ups OFF) and try again.");
        URL.revokeObjectURL(url);
        return;
      }
      const tryPrint = () => { try { w.focus(); w.print(); } catch {} };
      setTimeout(tryPrint, 800);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(`Test label fetch failed: ${e.message}`);
    }
  };

  const configured = !!status?.configured;
  const isAirPrint = mode === "airprint";

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="printer-setup-panel">
      <div className="flex items-center gap-2 mb-1">
        <Printer className="w-4 h-4 text-blue-600" />
        <h2 className="font-display text-lg font-bold">Visitor Badge Printer</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">Pick how each iPad / tablet should send labels to the Brother printer.</p>

      {/* Print-mode picker */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="print-mode-picker">
        <button
          onClick={() => switchMode("airprint")}
          className={`text-left rounded-xl border-2 p-4 transition ${
            isAirPrint ? "border-blue-500 bg-blue-50/60 shadow-sm" : "border-slate-200 hover:border-slate-300"
          }`}
          data-testid="mode-airprint"
        >
          <div className="flex items-center gap-2 mb-1">
            <Wifi className="w-4 h-4 text-blue-600" />
            <span className="font-display font-bold text-slate-900">AirPrint (recommended)</span>
            {isAirPrint && <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Active</span>}
          </div>
          <p className="text-xs text-slate-600">Every iPad / tablet on the same Wi-Fi prints to the printer directly. No pairing limit — unlimited devices.</p>
        </button>
        <button
          onClick={() => switchMode("cloud")}
          className={`text-left rounded-xl border-2 p-4 transition ${
            !isAirPrint ? "border-blue-500 bg-blue-50/60 shadow-sm" : "border-slate-200 hover:border-slate-300"
          }`}
          data-testid="mode-cloud"
        >
          <div className="flex items-center gap-2 mb-1">
            <Printer className="w-4 h-4 text-slate-600" />
            <span className="font-display font-bold text-slate-900">Cloud IP (advanced)</span>
            {!isAirPrint && <span className="ml-auto text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Active</span>}
          </div>
          <p className="text-xs text-slate-600">Server-side print. Only works if the printer has a public IP or a VPN to the cloud. Will fail with a private LAN IP.</p>
        </button>
      </div>

      {/* AirPrint instructions */}
      {isAirPrint && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 mb-4 text-sm text-blue-900" data-testid="airprint-help">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="space-y-1.5">
              <div><strong>How AirPrint works on your QL-810W:</strong></div>
              <ol className="list-decimal list-inside text-xs space-y-1 text-blue-800">
                <li>Make sure the printer is on the same Wi-Fi as every iPad / PC that needs to print.</li>
                <li>On each iPad once: <strong>Settings → Wi-Fi</strong> → confirm camp Wi-Fi connected.</li>
                <li>When a visitor signs in, the iPad shows their badge and pops the iOS Print dialog. Tap the Brother printer once, then tap Print.</li>
                <li>iOS remembers your printer choice, so on subsequent prints from the same iPad it's just one tap.</li>
              </ol>
              <div className="mt-2"><strong>No IP, no pairing, no password.</strong> The printer just needs to be on Wi-Fi.</div>
            </div>
          </div>
          <div className="mt-3">
            <button
              onClick={testPrintAirPrint}
              className="btn-touch px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold inline-flex items-center gap-2"
              data-testid="airprint-test-btn"
            >
              <TestTube2 className="w-4 h-4" /> Open AirPrint Test Page
            </button>
            <p className="text-[11px] text-blue-700 mt-1.5">Opens a test label and triggers the iOS print dialog. If the Brother shows up in the list, you're good to go.</p>
          </div>
        </div>
      )}

      {/* Label paper info */}
      {isAirPrint && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4 text-sm text-amber-900" data-testid="label-paper-help">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
            <div className="space-y-1.5">
              <div><strong>Badges print in portrait — photo on top, name + date below.</strong></div>
              <ul className="list-disc list-inside text-xs space-y-1 text-amber-800">
                <li><strong>Best:</strong> Brother <code className="bg-amber-100 px-1 rounded">DK-1208</code> (62 × 100 mm portrait labels) — fills the entire label.</li>
                <li><strong>Also works:</strong> <code className="bg-amber-100 px-1 rounded">DK-2205</code> continuous 62 mm tape — auto-cut to ~100 mm length.</li>
                <li><strong>Won't fill the label:</strong> landscape rolls like DK-1201 (3.5″ × 1.1″) — the badge will be tiny and letterboxed.</li>
              </ul>
              <div className="mt-2 text-xs">
                On the iOS Print dialog, expand <em>Options</em> → <strong>Orientation: Portrait</strong> → <strong>Paper Size:</strong> match your loaded roll (e.g. <code className="bg-amber-100 px-1 rounded">62 × 100 mm</code>).
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cloud-IP mode controls (legacy) */}
      {!isAirPrint && (
        <>
          <div className={`mb-4 px-3 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-semibold ${configured ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`} data-testid="printer-status-badge">
            {configured ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {configured ? `Configured (${status?.ip})` : "Not configured — visitors get manual badges"}
            {status?.source_ip === "env" && <span className="ml-2 text-[10px] uppercase tracking-[0.12em] text-slate-500">via env var</span>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1 sm:col-span-3">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Printer IP address or hostname</span>
              <input
                type="text" value={ip} onChange={(e) => setIp(e.target.value)}
                placeholder="e.g. 192.168.1.45 (private IPs won't work from cloud)"
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono"
                data-testid="printer-ip-input"
              />
              <span className="text-[11px] text-slate-500">
                ⚠ Cloud mode can only reach <strong>public</strong> IPs / hostnames. For a printer on your camp's Wi-Fi, use AirPrint mode instead.
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Model</span>
              <select value={model} onChange={(e) => setModel(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white" data-testid="printer-model-select">
                {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Label size</span>
              <select value={labelSize} onChange={(e) => setLabelSize(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white" data-testid="printer-label-select">
                {LABEL_SIZES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={save} disabled={saving} className="btn-touch px-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-2" data-testid="printer-save-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={testPrintCloud} disabled={!configured || testing} className="btn-touch px-4 rounded-xl bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-2" data-testid="printer-test-btn">
              {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
              {testing ? "Sending…" : "Send Cloud Test Print"}
            </button>
            <button onClick={() => { setIp(""); }} className="text-xs text-slate-500 hover:text-slate-700 underline self-center">clear IP</button>
          </div>
        </>
      )}
    </section>
  );
}
